import { useQueries, useQuery } from '@tanstack/react-query'
import { centerOfMass } from '@turf/turf'
import { useMemo } from 'react'
import type { IconName } from '../components/Icon.tsx'
import type { Asset, AssetType } from './assets.ts'
import { assetQuery } from './assets.ts'
import { type Device, type DeviceStatus, deviceListQuery, getDeviceLocation } from './devices.ts'
import { useImpersonation } from './impersonation.ts'
import { useSubscription } from './service.ts'

export const useLocations = () => {
	const impersonation = useImpersonation()
	const { data: subscription } = useSubscription()
	return useQueries({
		queries: [
			assetQuery(impersonation, { enabled: !!subscription?.features.includes('protections') }),
			deviceListQuery(impersonation?.partyAccountId),
		],
		combine: ([assetRes, deviceRes]) => ({
			isPending: assetRes.isLoading || deviceRes.isPending,
			data: [...(assetRes.data?.map(assetAsLocation) ?? []), ...(deviceRes.data?.map((device) => deviceAsLocation(device)) ?? [])],
		}),
	})
}

export const useAssetLocations = () => {
	const impersonation = useImpersonation()
	const { data: subscription } = useSubscription()
	const { data, isPending } = useQuery(assetQuery(impersonation, { enabled: !!subscription?.features.includes('protections') }))
	// Memoized so consumers can rely on the locations keeping their identity between
	// renders — `memo`'d rows and asset-keyed `useMemo`s depend on it, and the
	// per-asset centerOfMass isn't worth redoing on every render either
	const locations = useMemo(() => data?.map(assetAsLocation) ?? [], [data])
	return { isPending, data: locations }
}

export const assetAsLocation = (asset: Asset) => {
	const { id, name, vrn: assetVrn, site, type, description, metadata } = asset
	const [lon = 0, lat = 0] = centerOfMass(site.geom).geometry.coordinates
	const icon = locationIcons[type] ?? locationIcons.UNKNOWN
	return {
		type: 'asset' as const,
		id,
		name,
		icon,
		lat,
		lon,
		assetVrn,
		siteVrn: site.vrn,
		geometry: site.geom,
		assetType: type,
		description,
		metadata,
	}
}

export const deviceAsLocation = (device: Device, status?: DeviceStatus['status']) => {
	const { longitude: lon, latitude: lat } = getDeviceLocation(device) ?? { longitude: 0, latitude: 0 }
	return {
		type: 'device' as const,
		id: device.id,
		name: device.name ?? device.id,
		icon: locationIcons.DEVICE,
		lat,
		lon,
		status,
	}
}

export type Location = ReturnType<typeof useLocations>['data'][number]
export type AssetLocation = Location & { type: 'asset' }
export type DeviceLocation = Location & { type: 'device' }
export const isAssetLocation = (location: Location): location is AssetLocation => location.type === 'asset'
export const isDeviceLocation = (location: Location): location is DeviceLocation => location.type === 'device'

export const locationIcons = {
	AIRPORT: 'Plane',
	BUILDING: 'Building',
	CONST_SITE: 'VCrane',
	DEVICE: 'SignalStream',
	FARM: 'Farm',
	FIRE: 'Fire',
	FOREST: 'Trees',
	HELIPAD: 'Helicopter',
	LAKE: 'Water',
	MANUFACTURING: 'Industry',
	MINE: 'Shovel',
	NUCLEAR_FACILITY: 'CircleRadiation',
	OTHER: 'LocationDot',
	PARK: 'BenchTree',
	PORT: 'Anchor',
	POWER_PLANT: 'CircleBolt',
	RIVER: 'Water',
	ROAD: 'Road',
	ROUTE: 'Route',
	RUNWAY: 'PlaneDeparture',
	SOLAR_FARM: 'SolarPanel',
	STADIUM: 'CourtSport',
	SUBSTATION: 'TransformerBolt',
	TOWER: 'TowerCell',
	TRAIL: 'SignsPost',
	TRANSMISSION_LINE: 'VElecLine',
	TRANSMISSION_TERMINAL_STATION: 'VElecTerminal',
	UNKNOWN: 'LocationDot',
	WIND_FARM: 'WindTurbine',
	WIND_TURBINE: 'WindTurbine',
} as const satisfies Record<AssetType | 'DEVICE' | 'UNKNOWN', IconName>
